/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, {
	FunctionComponent,
	MouseEvent,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";

import { createPortal } from "react-dom";

import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

import { EditorMainContext } from "../../contexts/EditorMainContext";
import { useForkRef } from "../../hooks/useForkRef";
import { Backdrop } from "../Backdrop";

/**
 * Floating Container Anchor
 * Specifies which edge or corner to use as a point of origin.
 */
export enum FC_ANCHOR {
	LEFT_TOP = "left-top",
	CENTER_TOP = "center-top",
	RIGHT_TOP = "right-top",
	LEFT_CENTER = "left-center",
	RIGHT_CENTER = "right-center",
	LEFT_BOTTOM = "left-bottom",
	CENTER_BOTTOM = "center-bottom",
	RIGHT_BOTTOM = "right-bottom"
}

/**
 * Floating Container Direction
 * Specifies in which direction from anchor to place the container.
 */
export enum FC_DIRECTION {
	VR = "vr",
	HR = "hr"
}

/**
 * Floating Container Size
 * Specifies how to calculate container size:
 *   - AUTO = size is calculated automatically from the content
 *   - FIT = size is the same as parent's edge
 *   - FIT_MIN = min size is the same as parent's edge
 */
export enum FC_SIZE {
	AUTO = "auto",
	FIT = "fit",
	FIT_MIN = "fit-min"
}

/**
 * Floating Container Positioning
 * Secifies if it's possible to adjust position
 *   - AUTO = position can be adjusted based on available space (even the anchor can be flipped)
 *   - FIXED = position will be exactly at parent's edge, only clamped
 */
export enum FC_POSITION {
	AUTO = "auto",
	FIXED = "fixed"
}

/* Defaults */
const SAFE_PADDING_DEFAULT = 5;
const SIZE_DEFAULT = FC_SIZE.AUTO;
const POSITION_DEFAULT = FC_POSITION.AUTO;
const OFFSET_DEFAULT = 1;
const UPDATE_INTERVAL_DEFAULT = 250;

function invertAnchorHorizontal(anchor: FC_ANCHOR): FC_ANCHOR {
	switch (anchor) {
		case FC_ANCHOR.LEFT_TOP:
			return FC_ANCHOR.RIGHT_TOP;
		case FC_ANCHOR.LEFT_CENTER:
			return FC_ANCHOR.RIGHT_CENTER;
		case FC_ANCHOR.LEFT_BOTTOM:
			return FC_ANCHOR.RIGHT_BOTTOM;
		case FC_ANCHOR.RIGHT_TOP:
			return FC_ANCHOR.LEFT_TOP;
		case FC_ANCHOR.RIGHT_CENTER:
			return FC_ANCHOR.LEFT_CENTER;
		case FC_ANCHOR.RIGHT_BOTTOM:
			return FC_ANCHOR.LEFT_BOTTOM;
		default:
			return anchor;
	}
}

function invertAnchorVertical(anchor: FC_ANCHOR): FC_ANCHOR {
	switch (anchor) {
		case FC_ANCHOR.LEFT_TOP:
			return FC_ANCHOR.LEFT_BOTTOM;
		case FC_ANCHOR.CENTER_TOP:
			return FC_ANCHOR.CENTER_BOTTOM;
		case FC_ANCHOR.RIGHT_TOP:
			return FC_ANCHOR.RIGHT_BOTTOM;
		case FC_ANCHOR.LEFT_BOTTOM:
			return FC_ANCHOR.LEFT_TOP;
		case FC_ANCHOR.CENTER_BOTTOM:
			return FC_ANCHOR.CENTER_TOP;
		case FC_ANCHOR.RIGHT_BOTTOM:
			return FC_ANCHOR.RIGHT_TOP;
		default:
			return anchor;
	}
}

/**
 * Floating container props
 */
export interface IFloatingContainerProps {
	/** Parent element to place container at */
	parent: React.MutableRefObject<HTMLElement>;
	/** Parent element anchor to be used as a point of origin */
	anchor: FC_ANCHOR;
	/** Direction of the edge to place container at */
	direction: FC_DIRECTION;
	/** Container size - defaults to `FC_SIZE.AUTO` */
	size?: FC_SIZE;
	/** Container positioning - defaults to `FC_POSITION.AUTO` */
	position?: FC_POSITION;
	/** Safe padding from the screen edge - default to 5px */
	safePadding?: number;
	/** Offset to move container away from parent (in pixels) - defaults to 1px */
	offset?: number;
	/** Custom container classname */
	className?: string;
	/** If to add arrow element positioned to the parent */
	addArrow?: boolean;
	/** How often to check parent position - default to 250ms */
	updateInterval?: number;
	/** Ref to assign */
	containerRef?: React.MutableRefObject<HTMLElement>;
	/** Mouse enter event */
	onMouseEnter?: (ev: MouseEvent) => void;
	/** Mouse leave event */
	onMouseLeave?: (ev: MouseEvent) => void;
	/** Close event */
	onClose?: () => void;
	/** If backdrop should be used to close panel */
	allowBackdrop?: boolean;
}

/**
 * Floating container
 * Allows to position "floating" content bound to the parent element.
 *
 * @param props Component props
 */
export const FloatingContainer: FunctionComponent<IFloatingContainerProps> = ({
	parent,
	children,
	anchor,
	className,
	addArrow,
	updateInterval,
	direction,
	size: _size,
	position: _position,
	safePadding: _safePadding,
	offset: _offset,
	containerRef,
	onMouseEnter,
	onMouseLeave,
	onClose,
	allowBackdrop
}) => {
	const mainCtx = useContext(EditorMainContext);

	const cntElRef = useRef<HTMLDivElement>();
	const arrowElRef = useRef<HTMLSpanElement>();

	const forkRef = useForkRef(cntElRef, containerRef);

	const [ lastAnchor, setLastAnchor ] = useState<FC_ANCHOR>(null);
	const [ lastArrowOrientation, setLastArrowOrientation ] = useState<string>("unknown");

	useLayoutEffect(() => {
		if (!mainCtx.rootEl.current || !parent.current || !cntElRef.current) {
			return;
		}

		const size = _size || SIZE_DEFAULT;
		const position = _position || POSITION_DEFAULT;
		const safePadding = Math.max(0, _safePadding !== undefined ? _safePadding : SAFE_PADDING_DEFAULT);
		const offset = _offset !== undefined ? _offset : OFFSET_DEFAULT;

		let _lastAnchor = null;
		let _lastArrowOrientation = null;

		const updatePosition = () => {
			if (parent.current.offsetParent === null) {
				return;
			}

			const parentRect = parent.current.getBoundingClientRect();
			const editorRootRect = mainCtx.rootEl.current.getBoundingClientRect();

			const offsetX = parentRect.x - editorRootRect.x;
			const offsetY = parentRect.y - editorRootRect.y;

			let tWidth = cntElRef.current.scrollWidth;
			let tHeight = cntElRef.current.scrollHeight;

			let tAnchor = anchor;
			let left: number = null;
			let top: number = null;
			let right: number = null;
			let bottom: number = null;
			let width: number = null;
			let height: number = null;
			let minWidth: number = null;
			let minHeight: number = null;
			let arrowLeft: number = null;
			let arrowTop: number = null;
			let arrowRight: number = null;
			let arrowBottom: number = null;
			let arrowOrientation: string = null;

			const setCoords = (_anchor: FC_ANCHOR) => {
				left = null;
				top = null;
				right = null;
				bottom = null;

				switch (_anchor) {
					case FC_ANCHOR.LEFT_BOTTOM: {
						if (direction === FC_DIRECTION.HR) {
							left = Math.max(offsetX, safePadding);
							top = Math.max(offsetY + parentRect.height + offset, safePadding);
						} else if (direction === FC_DIRECTION.VR) {
							right = Math.max(editorRootRect.width - offsetX + offset, safePadding);
							bottom = Math.max(
								editorRootRect.height - offsetY - parentRect.height,
								safePadding
							);
						}
						break;
					}
					case FC_ANCHOR.CENTER_BOTTOM: {
						left = Math.round(
							Math.max(
								safePadding,
								Math.min(
									offsetX - (tWidth / 2 - parentRect.width / 2),
									editorRootRect.width - safePadding - tWidth
								)
							)
						);
						top = Math.max(offsetY + parentRect.height + offset, safePadding);
						break;
					}
					case FC_ANCHOR.RIGHT_BOTTOM: {
						if (direction === FC_DIRECTION.HR) {
							right = Math.max(editorRootRect.width - offsetX - parentRect.width, safePadding);
							top = Math.max(offsetY + parentRect.height + offset, safePadding);
						} else if (direction === FC_DIRECTION.VR) {
							left = Math.max(offsetX + parentRect.width + offset, safePadding);
							bottom = Math.max(
								editorRootRect.height - offsetY - parentRect.height,
								safePadding
							);
						}
						break;
					}
					case FC_ANCHOR.RIGHT_CENTER: {
						left = Math.max(offsetX + parentRect.width + offset, safePadding);
						top = Math.round(
							Math.max(
								safePadding,
								Math.min(
									offsetY - (tHeight / 2 - parentRect.height / 2),
									editorRootRect.height - safePadding - tHeight
								)
							)
						);
						break;
					}
					case FC_ANCHOR.RIGHT_TOP: {
						if (direction === FC_DIRECTION.HR) {
							right = Math.max(editorRootRect.width - offsetX - parentRect.width, safePadding);
							bottom = Math.max(editorRootRect.height - offsetY + offset, safePadding);
						} else if (direction === FC_DIRECTION.VR) {
							left = Math.max(offsetX + parentRect.width + offset, safePadding);
							top = Math.max(offsetY, safePadding);
						}
						break;
					}
					case FC_ANCHOR.CENTER_TOP: {
						left = Math.round(
							Math.max(
								safePadding,
								Math.min(
									offsetX - (tWidth / 2 - parentRect.width / 2),
									editorRootRect.width - safePadding - tWidth
								)
							)
						);
						bottom = Math.max(editorRootRect.height - offsetY + offset, safePadding);
						break;
					}
					case FC_ANCHOR.LEFT_TOP: {
						if (direction === FC_DIRECTION.HR) {
							left = Math.max(offsetX, safePadding);
							bottom = Math.max(editorRootRect.height - offsetY + offset, safePadding);
						} else if (direction === FC_DIRECTION.VR) {
							right = Math.max(editorRootRect.width - offsetX + offset, safePadding);
							top = Math.max(offsetY, safePadding);
						}
						break;
					}
					case FC_ANCHOR.LEFT_CENTER: {
						right = Math.max(editorRootRect.width - offsetX + offset, safePadding);
						top = Math.round(
							Math.max(
								safePadding,
								Math.min(
									offsetY - (tHeight / 2 - parentRect.height / 2),
									editorRootRect.height - safePadding - tHeight
								)
							)
						);
						break;
					}
				}
			};

			// 1) Set dimensions
			if (size === FC_SIZE.FIT) {
				if (direction === FC_DIRECTION.HR) {
					width = parentRect.width;
					tWidth = width;
				} else if (direction === FC_DIRECTION.VR) {
					height = parentRect.height;
					tHeight = height;
				}
			} else if (size === FC_SIZE.FIT_MIN) {
				if (direction === FC_DIRECTION.HR) {
					minWidth = parentRect.width;
					tWidth = Math.max(tWidth, minWidth);
				} else if (direction === FC_DIRECTION.VR) {
					minHeight = parentRect.height;
					tHeight = Math.max(tHeight, minHeight);
				}
			}

			// 2) Calculate base position
			setCoords(anchor);

			// 3) If position is auto, check if fits and adjust anchor
			if (position === FC_POSITION.AUTO) {
				const freeSpaceX = editorRootRect.width - offsetX - parentRect.width - safePadding;
				const freeSpaceY = editorRootRect.height - offsetY - parentRect.height - safePadding;
				const safeOffsetX = offsetX - safePadding;
				const safeOffsetY = offsetY - safePadding;

				if (left !== null && left + tWidth > editorRootRect.width - safePadding) {
					if (safeOffsetX > freeSpaceX) {
						tAnchor = invertAnchorHorizontal(tAnchor);
					}
				} else if (right !== null && right + tWidth > editorRootRect.width - safePadding) {
					if (freeSpaceX > safeOffsetX) {
						tAnchor = invertAnchorHorizontal(tAnchor);
					}
				}

				if (top !== null && top + tHeight > editorRootRect.height - safePadding) {
					if (safeOffsetY > freeSpaceY) {
						tAnchor = invertAnchorVertical(tAnchor);
					}
				} else if (bottom !== null && bottom + tHeight > editorRootRect.height - safePadding) {
					if (freeSpaceY > safeOffsetY) {
						tAnchor = invertAnchorVertical(tAnchor);
					}
				}

				if (tAnchor !== anchor) {
					setCoords(tAnchor);
				}
			}

			// 4) Clamp dimensions
			if (left !== null && left + tWidth >= editorRootRect.width - safePadding) {
				minWidth = null;
				width = editorRootRect.width - safePadding - left;
			} else if (right !== null && right + tWidth >= editorRootRect.width - safePadding) {
				minWidth = null;
				width = editorRootRect.width - safePadding - right;
			}

			if (top !== null && top + tHeight >= editorRootRect.height - safePadding) {
				minHeight = null;
				height = editorRootRect.height - safePadding - top;
			} else if (bottom !== null && bottom + tHeight >= editorRootRect.height - safePadding) {
				minHeight = null;
				height = editorRootRect.height - safePadding - bottom;
			}

			// Calculate arrow position
			switch (tAnchor) {
				case FC_ANCHOR.LEFT_BOTTOM: {
					if (direction === FC_DIRECTION.HR) {
						arrowLeft = offsetX - left + parentRect.width / 2;
						arrowTop = 0;
						arrowOrientation = "top";
					} else if (direction === FC_DIRECTION.VR) {
						arrowRight = 0;
						arrowBottom =
							editorRootRect.height -
							offsetY -
							parentRect.height -
							bottom +
							parentRect.height / 2;
						arrowOrientation = "right";
					}
					break;
				}
				case FC_ANCHOR.CENTER_BOTTOM: {
					arrowLeft = offsetX - left + parentRect.width / 2;
					arrowTop = 0;
					arrowOrientation = "top";
					break;
				}
				case FC_ANCHOR.RIGHT_BOTTOM: {
					if (direction === FC_DIRECTION.HR) {
						arrowRight =
							editorRootRect.width - offsetX - parentRect.width - right + parentRect.width / 2;
						arrowTop = 0;
						arrowOrientation = "top";
					} else if (direction === FC_DIRECTION.VR) {
						arrowLeft = 0;
						arrowBottom =
							editorRootRect.height -
							offsetY -
							parentRect.height -
							bottom +
							parentRect.height / 2;
						arrowOrientation = "left";
					}
					break;
				}
				case FC_ANCHOR.RIGHT_CENTER: {
					arrowLeft = 0;
					arrowTop = offsetY - top + parentRect.height / 2;
					arrowOrientation = "left";
					break;
				}
				case FC_ANCHOR.RIGHT_TOP: {
					if (direction === FC_DIRECTION.HR) {
						arrowRight =
							editorRootRect.width - offsetX - parentRect.width - right + parentRect.width / 2;
						arrowBottom = 0;
						arrowOrientation = "down";
					} else if (direction === FC_DIRECTION.VR) {
						arrowLeft = 0;
						arrowTop = offsetY - top + parentRect.height / 2;
						arrowOrientation = "left";
					}
					break;
				}
				case FC_ANCHOR.CENTER_TOP: {
					arrowLeft = offsetX - left + parentRect.width / 2;
					arrowBottom = 0;
					arrowOrientation = "down";
					break;
				}
				case FC_ANCHOR.LEFT_TOP: {
					if (direction === FC_DIRECTION.HR) {
						arrowLeft = offsetX - left + parentRect.width / 2;
						arrowBottom = 0;
						arrowOrientation = "down";
					} else if (direction === FC_DIRECTION.VR) {
						arrowRight = 0;
						arrowTop = offsetY - top + parentRect.height / 2;
						arrowOrientation = "right";
					}
					break;
				}
				case FC_ANCHOR.LEFT_CENTER: {
					arrowRight = 0;
					arrowTop = offsetY - top + parentRect.height / 2;
					arrowOrientation = "right";
					break;
				}
			}

			// Assign styles
			cntElRef.current.style.left = left !== null ? left + "px" : "";
			cntElRef.current.style.top = top !== null ? top + "px" : "";
			cntElRef.current.style.right = right !== null ? right + "px" : "";
			cntElRef.current.style.bottom = bottom !== null ? bottom + "px" : "";

			cntElRef.current.style.width = width !== null ? width + "px" : "";
			cntElRef.current.style.height = height !== null ? height + "px" : "";
			cntElRef.current.style.minWidth = minWidth !== null ? minWidth + "px" : "";
			cntElRef.current.style.minHeight = minHeight !== null ? minHeight + "px" : "";

			if (arrowElRef.current) {
				arrowElRef.current.style.left = arrowLeft !== null ? arrowLeft + "px" : "";
				arrowElRef.current.style.right = arrowRight !== null ? arrowRight + "px" : "";
				arrowElRef.current.style.top = arrowTop !== null ? arrowTop + "px" : "";
				arrowElRef.current.style.bottom = arrowBottom !== null ? arrowBottom + "px" : "";
			}

			if (_lastAnchor !== tAnchor) {
				_lastAnchor = tAnchor;
				setLastAnchor(tAnchor);
			}

			if (_lastArrowOrientation !== arrowOrientation) {
				_lastArrowOrientation = arrowOrientation;
				setLastArrowOrientation(arrowOrientation);
			}
		};

		updatePosition();
		const initTimeout = setTimeout(() => updatePosition(), 25);
		const updateTimer =
			updateInterval !== 0
				? setInterval(
						() => updatePosition(),
						updateInterval !== undefined ? updateInterval : UPDATE_INTERVAL_DEFAULT
				  )
				: null;

		return () => {
			clearTimeout(initTimeout);

			if (updateTimer) {
				clearInterval(updateTimer);
			}
		};
	}, [
		mainCtx.rootEl.current,
		cntElRef.current,
		parent.current,
		updateInterval,
		anchor,
		direction,
		_size,
		_position
	]);

	if (!mainCtx.rootEl.current || !parent.current) {
		return null;
	}

	const baseClass = BEM("edt-floating-container");
	baseClass.add("edt-base");

	if (lastAnchor) {
		baseClass.mod("anchor-" + lastAnchor + "-" + direction);
	}

	const showBackdrop = useMemo(() => allowBackdrop && isFunction(onClose), [ allowBackdrop, onClose ]);

	if (className) {
		baseClass.add(className);
	}

	return createPortal(
		<>
			<div
				className={baseClass.toClassName()}
				ref={forkRef}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
			>
				{addArrow ? (
					<span
						ref={arrowElRef}
						className={baseClass
							.element("arrow")
							.mod("orientation-" + lastArrowOrientation)
							.toClassName()}
					/>
				) : null}
				<div className={baseClass.element("inner").toClassName()}>{children}</div>
			</div>
			{showBackdrop && <Backdrop onClick={onClose} />}
		</>,
		mainCtx.rootEl.current
	);
};
